import { Link, useNavigate } from 'react-router-dom';
import {
  Menu,
  CloudDownload,
  ListTodo,
  CheckCircle,
  CloudUpload,
  RotateCcw,
  AlertTriangle,
  Settings,
  LogOut,
  Loader2,
  RefreshCcw,
  Moon,
  Sun,
} from 'lucide-react';
import { SidebarItem } from './SidebarItem';
import { useAuthStore } from '../../store/authStore';
import { useCasesStore } from '../../store/casesStore';
import { useUIStore } from '../../store/uiStore';
import { useDashboardStats } from '../../hooks/useDashboardStats';
import { useQuickStats } from '../../hooks/useQuickStats';
import { useSalesforceCountdown } from '../../hooks/useSalesforcePolling';
import { useLLMProviders } from '../../hooks/useLLMProviders';
import { usePromptList } from '../../hooks/usePrompts';
import { getProviderLogoPath, getProviderLogoAlt } from '../../utils/providerLogos';

export type DashboardSection = 'new' | 'queue' | 'approved' | 'uploaded' | 'returned' | 'issues' | 'other';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  activeSection?: DashboardSection;
  onSectionChange?: (section: DashboardSection) => void;
}

// Counts are now fetched internally via useDashboardStats

export function Sidebar({
  isCollapsed,
  onToggle,
  activeSection,
  onSectionChange,
}: SidebarProps) {
  const { logout } = useAuthStore();
  const { isFetchingSalesforce, fetchSalesforceCases } = useCasesStore();
  const { theme, setTheme } = useUIStore();
  const { countdown, reset: resetCountdown } = useSalesforceCountdown();
  const navigate = useNavigate();

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  // Manual sync handler
  const handleManualSync = () => {
    fetchSalesforceCases();
    resetCountdown();
  };

  // Fetch stats for sidebar badges
  const { stats } = useDashboardStats();
  const { stats: quickStats } = useQuickStats();

  // Fetch active provider and prompt
  const { activeProvider } = useLLMProviders();
  const { activePrompt } = usePromptList();

  // Convert stats to counts format (new_in_salesforce not included - requires SF API call)
  const counts = {
    queue: stats?.queue ?? 0,
    approved: stats?.approved ?? 0,
    uploaded: stats?.uploaded ?? 0,
    returned: stats?.returned ?? 0,
    issues: stats?.issues ?? 0,
  };

  // Handle section change - navigate to home if not on dashboard
  const handleSectionChange = (section: DashboardSection) => {
    if (onSectionChange) {
      onSectionChange(section);
    } else {
      // Navigate to dashboard with section
      navigate(`/?section=${section}`);
    }
  };

  return (
    <aside
      data-onboarding="sidebar"
      className={`
        flex flex-col bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transition-all duration-300 h-screen sticky top-0 overflow-hidden flex-shrink-0
        ${isCollapsed ? 'w-16' : 'w-56'}
      `}
    >
      {/* Header with hamburger and logo */}
      <div className={`flex items-center gap-3 px-3 py-4 border-b border-gray-200 dark:border-gray-800 ${isCollapsed ? 'justify-center' : ''}`}>
        <button
          onClick={onToggle}
          data-onboarding="sidebar-toggle"
          className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors flex-shrink-0"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Menu className="w-5 h-5" />
        </button>
        {!isCollapsed && (
          <Link to="/" className="flex items-center gap-2 no-underline">
            <img
              src="/tec-logo-circle.png"
              alt="TEC Logo"
              className="w-8 h-8 object-contain"
            />
            <span className="text-gray-900 dark:text-white font-semibold whitespace-nowrap">Translator</span>
          </Link>
        )}
      </div>

      {/* Pipeline Section */}
      <div className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden py-4">
        {!isCollapsed && (
          <div className="px-4 mb-2">
            <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              Pipeline
            </span>
          </div>
        )}
        <nav className="px-2 space-y-1" data-onboarding="sidebar-sections">
          <SidebarItem
            icon={<CloudDownload className="w-5 h-5" />}
            label="New in SF"
            isCollapsed={isCollapsed}
            isActive={activeSection === 'new'}
            onClick={() => handleSectionChange('new')}
          />
          <SidebarItem
            icon={<ListTodo className="w-5 h-5" />}
            label="Queue"
            badge={counts.queue}
            isCollapsed={isCollapsed}
            isActive={activeSection === 'queue'}
            onClick={() => handleSectionChange('queue')}
          />
          <SidebarItem
            icon={<CheckCircle className="w-5 h-5" />}
            label="Approved"
            badge={counts.approved}
            isCollapsed={isCollapsed}
            isActive={activeSection === 'approved'}
            onClick={() => handleSectionChange('approved')}
          />
          <SidebarItem
            icon={<CloudUpload className="w-5 h-5" />}
            label="Uploaded"
            badge={counts.uploaded}
            isCollapsed={isCollapsed}
            isActive={activeSection === 'uploaded'}
            onClick={() => handleSectionChange('uploaded')}
          />
          <SidebarItem
            icon={<RotateCcw className="w-5 h-5" />}
            label="Returned"
            badge={counts.returned}
            badgeVariant={counts.returned > 0 ? 'warning' : 'default'}
            isCollapsed={isCollapsed}
            isActive={activeSection === 'returned'}
            onClick={() => handleSectionChange('returned')}
          />
          <SidebarItem
            icon={<AlertTriangle className="w-5 h-5" />}
            label="Issues"
            badge={counts.issues}
            badgeVariant={counts.issues > 0 ? 'warning' : 'default'}
            isCollapsed={isCollapsed}
            isActive={activeSection === 'issues'}
            onClick={() => handleSectionChange('issues')}
          />
        </nav>

        {/* Tools Section */}
        {!isCollapsed && (
          <div className="px-4 mt-6 mb-2">
            <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              Tools
            </span>
          </div>
        )}
        {isCollapsed && <div className="my-4 mx-2 border-t border-gray-200 dark:border-gray-800" />}

        {/* Processing Status */}
        {!isCollapsed ? (
          <div className="px-3 mb-3" data-onboarding="processing-status">
            <div className={`rounded-lg p-3 ${(quickStats?.processing ?? 0) > 0 ? 'bg-purple-100 dark:bg-purple-900/50' : 'bg-gray-100 dark:bg-gray-800'}`}>
              <div className="flex items-center gap-2">
                <Loader2 className={`w-4 h-4 ${(quickStats?.processing ?? 0) > 0 ? 'text-purple-500 dark:text-purple-400 animate-spin' : 'text-gray-400 dark:text-gray-500'}`} />
                <span className="text-sm text-gray-600 dark:text-gray-300">Processing</span>
                <span className={`ml-auto text-sm font-semibold ${(quickStats?.processing ?? 0) > 0 ? 'text-purple-500 dark:text-purple-400' : 'text-gray-400 dark:text-gray-500'}`}>
                  {quickStats?.processing ?? 0}
                </span>
              </div>
              {(quickStats?.errors ?? 0) > 0 && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <AlertTriangle className="w-4 h-4 text-red-500 dark:text-red-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">Errors</span>
                  <span className="ml-auto text-sm font-semibold text-red-500 dark:text-red-400">
                    {quickStats?.errors ?? 0}
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="px-2 mb-3 group relative">
            <div className={`flex justify-center p-2 rounded-lg ${(quickStats?.processing ?? 0) > 0 ? 'bg-purple-100 dark:bg-purple-900/50' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
              <div className="relative">
                <Loader2 className={`w-5 h-5 ${(quickStats?.processing ?? 0) > 0 ? 'text-purple-500 dark:text-purple-400 animate-spin' : 'text-gray-400 dark:text-gray-500'}`} />
                {(quickStats?.processing ?? 0) > 0 && (
                  <span className="absolute -bottom-1 -right-2.5 min-w-[18px] h-[18px] text-[10px] rounded-full font-semibold flex items-center justify-center bg-purple-200 dark:bg-purple-500/30 text-purple-600 dark:text-purple-300">
                    {quickStats?.processing}
                  </span>
                )}
              </div>
            </div>
            {/* Tooltip for collapsed state */}
            <div className="absolute left-full ml-2 top-0 px-3 py-2 bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
              <div className="font-medium">Processing: {quickStats?.processing ?? 0}</div>
              {(quickStats?.errors ?? 0) > 0 && (
                <div className="text-xs text-red-400">Errors: {quickStats?.errors}</div>
              )}
            </div>
          </div>
        )}

        {/* Provider/Model/Prompt Display */}
        {!isCollapsed ? (
          <div className="px-3 mb-3" data-onboarding="active-provider">
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <img
                  src={getProviderLogoPath(activeProvider?.name)}
                  alt={getProviderLogoAlt(activeProvider?.name)}
                  className="w-5 h-5 object-contain dark:brightness-0 dark:invert"
                />
                <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                  {activeProvider?.name || 'Loading...'}
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate mb-1">
                {activeProvider?.model || '...'}
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500 truncate">
                Prompt: {activePrompt?.name || '...'}
              </div>
            </div>
          </div>
        ) : (
          <div className="px-2 mb-3 group relative">
            <div className="flex justify-center p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 cursor-default">
              <img
                src={getProviderLogoPath(activeProvider?.name)}
                alt={getProviderLogoAlt(activeProvider?.name)}
                className="w-5 h-5 object-contain dark:brightness-0 dark:invert"
              />
            </div>
            {/* Tooltip for collapsed state */}
            <div className="absolute left-full ml-2 top-0 px-3 py-2 bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
              <div className="font-medium capitalize">{activeProvider?.name || 'Loading...'}</div>
              <div className="text-xs text-gray-400">{activeProvider?.model || '...'}</div>
              <div className="text-xs text-gray-500">Prompt: {activePrompt?.name || '...'}</div>
            </div>
          </div>
        )}

        <nav className="px-2 space-y-1">
          <div data-onboarding="settings-link">
            <SidebarItem
              to="/settings"
              icon={<Settings className="w-5 h-5" />}
              label="Settings"
              isCollapsed={isCollapsed}
            />
          </div>
        </nav>

        {/* Theme Toggle */}
        {!isCollapsed ? (
          <div className="px-3 mt-3" data-onboarding="theme-toggle">
            <button
              onClick={toggleTheme}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white rounded-lg transition-colors"
            >
              {theme === 'light' ? (
                <>
                  <Moon className="w-5 h-5" />
                  <span>Dark Mode</span>
                </>
              ) : (
                <>
                  <Sun className="w-5 h-5" />
                  <span>Light Mode</span>
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="px-2 mt-3 group relative">
            <button
              onClick={toggleTheme}
              className="w-full flex justify-center p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white rounded-lg transition-colors"
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5" />
              ) : (
                <Sun className="w-5 h-5" />
              )}
            </button>
            {/* Tooltip */}
            <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
              {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
            </div>
          </div>
        )}

        {/* SF Sync button with countdown - aligned at bottom */}
        <div className="mt-auto px-2 pt-4" data-onboarding="sync-btn">
          {!isCollapsed ? (
            <button
              onClick={handleManualSync}
              disabled={isFetchingSalesforce}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white rounded-lg transition-colors disabled:cursor-not-allowed"
            >
              <RefreshCcw className={`w-5 h-5 ${isFetchingSalesforce ? 'animate-spin' : ''}`} />
              <span>{isFetchingSalesforce ? 'Syncing...' : `Sync Salesforce (${countdown}s)`}</span>
            </button>
          ) : (
            <button
              onClick={handleManualSync}
              disabled={isFetchingSalesforce}
              className="w-full flex justify-center p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white rounded-lg transition-colors disabled:cursor-not-allowed group relative"
            >
              <div className="relative w-5 h-5 flex items-center justify-center">
                <RefreshCcw className={`w-5 h-5 ${isFetchingSalesforce ? 'animate-spin' : ''}`} />
                {!isFetchingSalesforce && (
                  <span className="absolute inset-0 flex items-center justify-center text-[8px] font-medium">
                    {countdown}
                  </span>
                )}
              </div>
              {/* Tooltip */}
              <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                {isFetchingSalesforce ? 'Syncing...' : `Sync Salesforce (${countdown}s)`}
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 dark:border-gray-800 p-2">
        <div data-onboarding="logout-btn">
          <SidebarItem
            icon={<LogOut className="w-5 h-5" />}
            label="Logout"
            isCollapsed={isCollapsed}
            onClick={logout}
          />
        </div>
        {!isCollapsed && (
          <div className="mt-3 px-3 pb-2">
            <p className="text-[10px] text-gray-400 dark:text-gray-600 text-center">
              The Evaluation Company&trade;
            </p>
          </div>
        )}
      </div>
    </aside>
  );
}
